/**
 * Signup layout / structure controls — the venue-configurable shape of the
 * public signup, stored in `venue.signup_layout` (a JSONB object). This covers
 * everything the colours/copy tokens can't: whether the branded hero panel
 * shows at all, which side it sits on, how wide it is, what it contains, the
 * shell's corner treatment and the form's alignment.
 *
 * Unset ({} / null) resolves to the built-in defaults, so a venue that never
 * touches this looks exactly as the split-hero shipped. Shared by the public
 * form (render) and the admin/client Branding editors (controls) so they can't
 * drift.
 */

export const DEFAULT_LAYOUT = {
  heroEnabled: true,        // show the branded hero panel at all
  heroPosition: "left",     // left | right  (desktop side; mobile is always a top band)
  heroWidth: "medium",      // narrow | medium | wide
  heroShowLogo: true,       // show the logo tile in the hero
  heroShowBenefits: true,   // show the value-prop list in the hero
  heroGradient: true,       // gradient wash vs. flat accent fill
  heroCoverOpacity: 0.14,   // 0..0.6 — strength of the hero background image
  corner: "rounded",        // rounded | soft | sharp  (shell/card corners)
  formAlign: "center",      // center | left  (form content within its panel)
};

export const HERO_POSITION_OPTIONS = [
  { value: "left", label: "Left" },
  { value: "right", label: "Right" },
];
export const HERO_WIDTH_OPTIONS = [
  { value: "narrow", label: "Narrow" },
  { value: "medium", label: "Medium" },
  { value: "wide", label: "Wide" },
];
export const CORNER_OPTIONS = [
  { value: "rounded", label: "Rounded" },
  { value: "soft", label: "Soft" },
  { value: "sharp", label: "Square" },
];
export const FORM_ALIGN_OPTIONS = [
  { value: "center", label: "Centred" },
  { value: "left", label: "Left" },
];

const ENUMS = {
  heroPosition: ["left", "right"],
  heroWidth: ["narrow", "medium", "wide"],
  corner: ["rounded", "soft", "sharp"],
  formAlign: ["center", "left"],
};

/** Merge a venue's stored layout over the defaults and sanitise every field. */
export function resolveLayout(venue) {
  const raw = venue && typeof venue.signup_layout === "object" && !Array.isArray(venue.signup_layout)
    ? venue.signup_layout
    : {};
  const l = { ...DEFAULT_LAYOUT, ...raw };
  for (const key of Object.keys(ENUMS)) {
    if (!ENUMS[key].includes(l[key])) l[key] = DEFAULT_LAYOUT[key];
  }
  // Booleans default true — only an explicit `false` turns something off.
  l.heroEnabled = l.heroEnabled !== false;
  l.heroShowLogo = l.heroShowLogo !== false;
  l.heroShowBenefits = l.heroShowBenefits !== false;
  l.heroGradient = l.heroGradient !== false;
  let op = Number(l.heroCoverOpacity);
  if (!Number.isFinite(op)) op = DEFAULT_LAYOUT.heroCoverOpacity;
  l.heroCoverOpacity = Math.max(0, Math.min(0.6, op));
  return l;
}

/** Modifier classes for the `.signup-shell` from a resolved layout. */
export function shellClasses(layout) {
  const c = ["signup-shell"];
  if (!layout.heroEnabled) {
    c.push("su-no-hero");
  } else {
    c.push(`su-hero-${layout.heroPosition}`);
    c.push(`su-hw-${layout.heroWidth}`);
    if (!layout.heroGradient) c.push("su-hero-flat");
  }
  c.push(`su-corner-${layout.corner}`);
  c.push(`su-align-${layout.formAlign}`);
  return c.join(" ");
}
